import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, AfterViewInit } from '@angular/core';
import { SpeechEngineService, TTSMode, TTSStatus } from '../utils/text-to-speech/on-the-fly/speech-engine.service';
import { DisplayMode } from '../element-render-audio/model';
// STT UI and logic moved into stt-toolbar component
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { SttEngineService } from '../utils/speech-to-text/stt-engine.service';
import { ItemSetFrameworkCtrl } from '../../ui-item-maker/item-set-editor/controllers/framework';
import { IAssessmentFrameworkDetail } from '../../ui-item-maker/item-set-editor/models/assessment-framework';


enum EttsToolBarAction {
  PLAY = 'play',
  PAUSE = 'pause',
  STOP = 'stop',
  READ_FROM_SELECTION = 'read-from-selection'
}

@Component({
  selector: 'render-tts-audio-player',
  templateUrl: './render-tts-audio-player.component.html',
  styleUrls: ['./render-tts-audio-player.component.scss']
})
export class RenderTtsAudioPlayerComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() currentQuestionId: number;
  @Input() speechMode: TTSMode = TTSMode.CLICK;
  @Input() audioUrl: string;
  @Input() defaultPlaybackSpeed: number = 1;
  @Input() displayMode = DisplayMode.NORMAL;
  @Input() hasControls: boolean = true;
  @Input() isQuestionAudio: boolean = true;
  @Input() frameworkCtrl: ItemSetFrameworkCtrl;
  @Input() asmtFmrk: IAssessmentFrameworkDetail;
  @Output() speechModeChange = new EventEmitter<TTSMode>();
  @Output() playbackRateChange = new EventEmitter<number>();
  @Output() speechToTextChange = new EventEmitter<boolean>();

  ttsToolBarAction = EttsToolBarAction;
  speechRate: number = 1.0;
  private readonly RATE_STEP = 0.25;
  showSettings = false;
  highlightColor: string = '#FFFF00';
  wordHighlightColor: string = '#0000FF';
  
  private destroy$ = new Subject<void>();

  private ttsPrepared = false;
  private currentTTSQuestionId: number | null = null;
  

  // For V1 compatibility
  DisplayMode = DisplayMode;

  

  /** All .question-container nodes found the *first* time Play is pressed */
  private containerQueue: HTMLElement[] = [];
  /** Index of the container currently being / about-to-be read */
  private currentContainerIdx = -1;
  /** Subscription for SpeechEngine status changes */
  private ttsStatusSub: Subscription;

  @ViewChild(CdkDrag) dragInstance: CdkDrag;
  @ViewChild('tooltipOverlay') overlayInstance: CdkConnectedOverlay;

  private dragSubscription: Subscription;

  

  constructor(
    private sttEngine: SttEngineService,
    public speechEngine: SpeechEngineService
  ) { }

  ngOnInit(): void {
    this.speechRate = this.speechEngine.getRate();
    this.highlightColor = this.speechEngine.getHighlightColor();
    this.wordHighlightColor = this.speechEngine.getWordHighlightColor();
    

    /* keep an ear on the SpeechEngine so we know when a container finishes */
    this.ttsStatusSub = this.speechEngine
      .getStatusObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => this.handleTtsStatusChange(status));
  }

  ngAfterViewInit(): void {
    // Ensure dragInstance is available before subscribing
    if (this.dragInstance) {
      this.dragSubscription = this.dragInstance.moved.subscribe(() => {
        // Check if overlay is defined and has an overlayRef
        if (this.overlayInstance && this.overlayInstance.overlayRef) {
          // Check if the overlay is currently attached/visible before updating
          if (this.overlayInstance.overlayRef.hasAttached()) {
             this.overlayInstance.overlayRef.updatePosition();
          }
        }
      });
    } else {
      console.warn('CdkDrag instance not found.'); // Optional: Add a warning if dragInstance isn't found
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Unsubscribe from the drag moved event to prevent memory leaks
    if (this.dragSubscription) {
      this.dragSubscription.unsubscribe();
    }
    if (this.ttsStatusSub) { this.ttsStatusSub.unsubscribe(); }
    this.speechEngine.stop();
  }

  ngOnChanges(): void {
    if (this.currentQuestionId !== this.currentTTSQuestionId) {
      this.resetTTS();
      this.currentTTSQuestionId = this.currentQuestionId;
      if (this.speechMode === TTSMode.HOVER) {
        // When the question changes, we need to re-prepare the containers for hover mode.
        // A timeout is used to ensure the new question's content has been rendered in the DOM.
        setTimeout(() => this.prepareHoverModeContainers(), 100);
      }
    }
  }

  onTtsToolbarAction(action: EttsToolBarAction) {
    switch(action) {
      case EttsToolBarAction.PLAY:  return this.startTTS();
      case EttsToolBarAction.PAUSE: return this.pauseTTS();
      case EttsToolBarAction.STOP:  return this.stopTTS();
    }
  }

  startTTS() {
    if (this.speechMode === TTSMode.HOVER) {
      return;
    }
    /* Lazily fill the queue the first time Play is pressed */
    if (this.containerQueue.length === 0) {
      this.containerQueue = Array.from(
        document.querySelectorAll<HTMLElement>('.question-container'));
      this.currentContainerIdx = 0;
    }

    // If there is a text selection, we should play from there.
    // This may involve switching the current container.
    const activeElement = document.activeElement;
    const selection = window.getSelection();

    // Check if the active element is a textarea and has a selection.
    if (activeElement && activeElement.tagName.toLowerCase() === 'textarea') {
      const textarea = activeElement as HTMLTextAreaElement;
      // Check if there is a selection in the textarea.
      if (textarea.selectionStart !== textarea.selectionEnd) {
        const containerIndex = this.containerQueue.findIndex(container => container.contains(textarea));
        if (containerIndex !== -1 && this.currentContainerIdx !== containerIndex) {
          // A different container is selected via textarea selection, so we must switch to it.
          this.currentContainerIdx = containerIndex;
          this.ttsPrepared = false; // Force re-preparation for the new container
        }
      }
    } else if (selection && !selection.isCollapsed) { // If there is a text selection, we should play from there.
        const range = selection.getRangeAt(0);
        const selectedNode = range.startContainer;

        if (selectedNode) {
            const containerIndex = this.containerQueue.findIndex(container => container.contains(selectedNode));
            if (containerIndex !== -1 && this.currentContainerIdx !== containerIndex) {
                // A different container is selected via text selection, so we must switch to it.
                this.currentContainerIdx = containerIndex;
                this.ttsPrepared = false; // Force re-preparation for the new container
            }
        }
    }

    if (this.containerQueue.length === 0) { return; }

    /* If we haven't prepared the current container yet, do so now */
    if (!this.ttsPrepared) {
      this.prepareAndPlayCurrentContainer();
    } else {
      /* Already prepared = we were paused → simply resume */
      this.speechEngine.play();
    }
  }

  pauseTTS() {
    this.speechEngine.pause();
  }

  stopTTS() {
    this.speechEngine.stop();
    this.resetSequence();
  }

  keepInputFocus(ev: MouseEvent) {
    ev.preventDefault();
  }

  resetTTS() {
    this.speechEngine.stop();
    this.resetSequence();
  }

  decreaseRate(): void {
    if (this.speechRate > 0.5) {
      this.speechRate = Math.max(0.5, this.speechRate - this.RATE_STEP);
      this.speechEngine.setRate(this.speechRate);
    }
  }

  increaseRate(): void {
    if (this.speechRate < 2) {
      this.speechRate = Math.min(2, this.speechRate + this.RATE_STEP);
      this.speechEngine.setRate(this.speechRate);
    }
  }

  toggleSettings(): void {
    this.showSettings = !this.showSettings;
  }

  onHighlightColorChange(color: string): void {
    this.highlightColor = color;
    this.speechEngine.setHighlightColor(color);
  }

  onWordHighlightColorChange(color: string): void {
    this.wordHighlightColor = color;
    this.speechEngine.setWordHighlightColor(color);
  }

  onToggleHoverMode(): void {
    const newMode = this.speechMode === TTSMode.HOVER ? TTSMode.CLICK : TTSMode.HOVER;
    this.speechMode = newMode;
    this.speechEngine.setMode(newMode);
    this.speechModeChange.emit(newMode);

    if (newMode === TTSMode.HOVER) {
      this.prepareHoverModeContainers();
    } else {
      this.stopTTS();
    }
  }

  // For V1 audio player compatibility
  onPlaybackRateChangeV1(rate: number): void {
    this.playbackRateChange.emit(rate);
  }

  private prepareHoverModeContainers(): void {
    this.containerQueue = Array.from(
      document.querySelectorAll<HTMLElement>('.question-container')
    );
    this.currentContainerIdx = 0;
    if (this.containerQueue.length > 0) {
      this.speechEngine
        .prepareContainers(this.containerQueue, true)
        .then(() => {
          this.ttsPrepared = true;
        })
        .catch(err => console.error('Error preparing TTS for hover:', err));
    }
  }


  isTTSV2Enabled(): boolean {
    if(this.frameworkCtrl) {
      return (this.frameworkCtrl.asmtFmrk?.toolbarOptions?.tts_v2) ?? false;
    }
    // Fallback to the assessment framework if this is from the test runner
    // This is to ensure that the TTS v2 is enabled even if the framework is not set
    return (this.asmtFmrk?.toolbarOptions?.tts_v2) ?? false;
  }

  isSTTEnabled(): boolean {
    if(this.frameworkCtrl) {
      return this.frameworkCtrl.asmtFmrk?.toolbarOptions?.stt ?? false;
    }
    // Fallback to the assessment framework if this is from the test runner
    return this.asmtFmrk?.toolbarOptions?.stt ?? false;
  }
  /** Called whenever SpeechEngine status changes */
  private handleTtsStatusChange(status: TTSStatus): void {
    /* We only care when it *finishes* the container we launched */
    if (status !== TTSStatus.IDLE || !this.ttsPrepared) { return; }

    /* Mark current container as done */
    this.ttsPrepared = false;

    if (this.currentContainerIdx < this.containerQueue.length - 1) {
      /* More containers remain → advance pointer and wait for next Play click */
      this.currentContainerIdx++;
    } else {
      /* Whole queue finished → tidy up */
      this.resetSequence();
    }
  }

  /** Prepares the container at currentContainerIdx and then calls play() */
  private prepareAndPlayCurrentContainer(): void {
    if(this.speechMode === TTSMode.CLICK) {
      const container = this.containerQueue[this.currentContainerIdx];
      this.speechEngine
        .prepareContainers([container], true)
        .then(() => {
          this.ttsPrepared = true;
          this.speechEngine.play();
        })
        .catch(err => console.error('Error preparing TTS:', err));
    }
    else if(this.speechMode === TTSMode.HOVER) {
      this.speechEngine
        .prepareContainers(this.containerQueue, true)
        .then(() => {
          this.ttsPrepared = true;
          this.speechEngine.play();
        })
        .catch(err => console.error('Error preparing TTS:', err));
    }
  }

  /** Clears queue & flags (called on Stop, question change, etc.) */
  private resetSequence(): void {
    this.containerQueue = [];
    this.currentContainerIdx = -1;
    this.ttsPrepared = false;
  }
}
